import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { verifyEmail, sendVerificationEmail } from '../api';
import { useAuth } from '../context/AuthContext';
import { AuthCard } from '../components/AuthCard';
import { readHashToken, clearHash } from '../utils/emailLinks';

// Opened from the verification email, signed in or out. The link works
// once, so the call is made a single time even when effects run twice.
function VerifyEmail() {
  const { user, loading: authLoading, setEmailVerified } = useAuth();
  const [token] = useState(() => readHashToken());
  const [status, setStatus] = useState(token ? 'verifying' : 'error');
  const [message, setMessage] = useState(token ? '' : 'This link is incomplete. Open it again from the email.');
  const [resendNote, setResendNote] = useState('');
  const [resending, setResending] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    clearHash();
    if (!token || started.current) return;
    started.current = true;
    verifyEmail(token)
      .then(res => {
        setMessage(res.data?.message || 'Your email is verified.');
        setStatus('verified');
      })
      .catch(err => {
        setMessage(err.response?.data?.error || (err.response
          ? 'This link is invalid or has expired.'
          : 'Could not reach the server. Check your connection and open the link again.'));
        setStatus('error');
      });
  }, [token]);

  // Once the session has loaded, a signed-in person's notice goes away.
  useEffect(() => {
    if (status === 'verified' && !authLoading && user) setEmailVerified(true);
  }, [status, authLoading, user, setEmailVerified]);

  const resend = async () => {
    setResending(true);
    setResendNote('');
    try {
      const res = await sendVerificationEmail();
      setResendNote(res.data?.sent === false
        ? 'Email is not set up on this server yet, so nothing was sent. Ask your administrator.'
        : (res.data?.message || 'We sent you a new verification email.'));
    } catch (err) {
      setResendNote(err.response?.data?.error || 'Could not send a new email. Try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthCard subtitle="Verify your email">
      {status === 'verifying' && (
        <div className="loading-container" aria-label="Verifying your email"><div className="spinner"></div></div>
      )}
      {status === 'verified' && <div className="alert alert-success" role="status">{message}</div>}
      {status === 'error' && <div className="alert alert-error" role="alert">{message}</div>}

      {status === 'error' && user && (
        <>
          <button type="button" className="btn btn-secondary login-btn" onClick={resend} disabled={resending}>
            {resending ? 'Sending...' : 'Send a new verification email'}
          </button>
          {resendNote && <p className="login-text" role="status" style={{ marginTop: 12 }}>{resendNote}</p>}
        </>
      )}

      {status !== 'verifying' && (
        <p className="login-request">
          {user ? <Link to="/">Continue to ActiveOptics</Link> : <Link to="/login">Sign in</Link>}
        </p>
      )}
    </AuthCard>
  );
}

export default VerifyEmail;
